import { forwardRef, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import { commonFormFields } from "../../FeuConstants";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { MDBCol, MDBRow } from "mdbreact";
import { Button, Label, TextArea } from "abs-ui-components";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuC23QualityAssurance = forwardRef(({ c23FuelTanks, refetchMonitoringData,vesselMstId }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);
    const savedValuesRef = useRef({});
    const { dirtyFields } = formState;
    const isC23Dirty = !!(dirtyFields?.c12 && Object.keys(dirtyFields.c12).length > 0);

    useEffect(() => {
        if (c23FuelTanks) {
            reset(prev => ({ ...prev, c12: c23FuelTanks }));
        }
    }, [c23FuelTanks, reset]);


    const c23Fields = useMemo(() => {
        // Map your frontend form field names to backend API keys
        const feildMapping = {
            procedureReference: "referenceToProcedure",
            procedureVersion: "versionOfProcedure",
            procedureDescription: "descriptionOfProcedure",
            responsiblePersonOrPosition: "responsiblePersonOrPosition",
            recordLocation: "recordLocation",
            itSystemName: "itSystemName",
        };
        return commonFormFields.reduce((acc, field) => {
            if (field.name === "dataSources") return acc;
            const mappedFeild = feildMapping[field.name] || field.name;

            // Override label if it's the description field
            const label =
                field.name === "procedureDescription"
                    ? "Description of procedure (a brief description of the procedure can be provided if already existing outside the monitoring plan)"
                    : field.label;

            // Push the transformed field into accumulator
            acc.push({
                ...field,
                name: mappedFeild,
                label,
            });

            return acc;
        }, []);
    }, []);


    AutoSave(ref, [], async () => {
        const currentValues = getValues("c23");
        const isChanged = Object.keys(currentValues || {}).some(
            key => currentValues[key] !== savedValuesRef.current?.[key]
        );
        if (!isC23Dirty || !isChanged) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("c12");
        const payload = {
            ...c23Fields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        if (formValues.id) {
            payload.id = formValues.id;
        }

        const isUpdate = !!formValues.id;
        try {
            const C23FuelBunkered = await callApi({
                method: "POST",
                url: API_ROUTES.C23(vesselMstId),
                data: payload,
            });
            if (C23FuelBunkered?.isSuccess) {
                emitToasterMsg("success", "Table C.2.3", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c1: formValues }));
                savedValuesRef.current = { ...formValues };
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                // emitToasterMsg("success", "Table C23", isUpdate ? "Updated successfully" : "Created successfully");
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.2.3", C23FuelBunkered?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.2.3", error.errorMessage);
        }
    };

    return (
        <div className="b7-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of procedure</h6>
                </MDBCol>
                <MDBCol md="4">
                    <h6 className="titles-font">Ensuring quality assurance of measuring equipment</h6>
                </MDBCol>
            </MDBRow>
            {c23Fields.map(({ label, name, maxLength }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`c12.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`c12_${name}`}
                                    className="textarea-resize"
                                    value={field?.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="c12_save_btn"
                    disabled={!isC23Dirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}>
                    Save
                </Button>
            </div>
        </div>
    );
})

export default FeuC23QualityAssurance;